import { useState, useEffect } from 'react';
import axios from 'axios';
import { socket } from '../../../admin/chat/socket';

const LOCATION_RECONCILIATION_MS = 60 * 1000;

/**
 * Custom hook to fetch and update driver location
 * Uses Socket.IO updates with bounded HTTP reconciliation.
 */
export function useDriverLocation(driverId) {
  const [driver, setDriver] = useState(null);

  useEffect(() => {
    if (!driverId) return;

    const API = process.env.REACT_APP_API_URL;
    let inFlight = false;
    let stopped = false;
    const controller = new AbortController();

    const fetchLocation = async () => {
      if (stopped || inFlight) return;
      
      inFlight = true;
      try {
        const response = await axios.get(`${API}/api/location/driver`, {
          params: { driver_id: driverId },
          signal: controller.signal,
        });
        
        if (!stopped) {
          setDriver(response.data);
        }
      } catch (error) {
        if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
          console.error('Error fetching driver location:', error);
        }
      } finally {
        inFlight = false;
      }
    };

    const handleLocationUpdate = payload => {
      const matchesDriver = [payload?.user_id, payload?.id, payload?.driver_profile_id]
        .filter(value => value != null)
        .some(value => Number(value) === Number(driverId));
      if (matchesDriver) setDriver(previous => ({ ...(previous || {}), ...payload }));
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchLocation();
    };
    const reconcile = () => {
      if (document.visibilityState === 'visible') fetchLocation();
    };

    fetchLocation();
    const interval = window.setInterval(reconcile, LOCATION_RECONCILIATION_MS);
    document.addEventListener('visibilitychange', handleVisibility);
    socket.on('connect', fetchLocation);
    socket.on('driver_location_updated', handleLocationUpdate);

    return () => {
      stopped = true;
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      socket.off('connect', fetchLocation);
      socket.off('driver_location_updated', handleLocationUpdate);
    };
  }, [driverId]);

  return driver;
}
